import React from 'react';
import classNames from 'classnames';
import { formatPrice, formatPriceCents, formatPriceCentsInMillions, formatPriceInMillions } from 'utils/number';
import { KpiTriangleUpIcon } from 'ui/Icons/components/KpiTriangleUp.component';
import { KpiTriangleDownIcon } from 'ui/Icons/components/KpiTriangleDown.component';
import { getCurrencySymbol } from 'utils';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';

interface IKpiCardsProps {
  totalBookings: number;
  totalValueCents: number;
  bookingsChangePercent: number;
  valueChangePercent: number;
}

const formatPercent = (value: number) => {
  const absValue = Math.abs(value);
  const hasDecimals = absValue % 1 !== 0;
  return `${formatPrice(absValue, hasDecimals ? 1 : 0)}%`;
};



interface IChangePillProps {
  changePercent: number;
}

const ChangePill: React.FC<IChangePillProps> = ({ changePercent }) => {
  const isPositiveOrZero = changePercent >= 0;

  return (
    <div className="flex items-center justify-center gap-[10px] w-full">
      <div
        className={classNames('flex items-center justify-center gap-[3px] p-[5px]', {
          'bg-teal-20': isPositiveOrZero,
          'bg-red-25 rounded-[4px]': !isPositiveOrZero,
        })}
      >
        {isPositiveOrZero ? (
          <KpiTriangleUpIcon className="w-[8px] h-[5px]" icon="status-confirmed" />
        ) : (
          <KpiTriangleDownIcon className="w-[8px] h-[5px]" icon="status-cancelled" />
        )}
        <p
          className={classNames(
            'm-0 font-pt-sans text-[13px] leading-normal tracking-[0.52px] text-center whitespace-nowrap tabular-nums',
            {
              'text-status-confirmed': isPositiveOrZero,
              'text-status-cancelled': !isPositiveOrZero,
            }
          )}
        >
          {formatPercent(changePercent)}
        </p>
      </div>
      <p className="m-0 font-pt-sans text-[13px] leading-normal tracking-[0.52px] text-center text-gray-100">
        vs. last period
      </p>
    </div>
  );
};

interface IKpiChangeIndicatorProps {
  currentValue: number;
  changePercent: number;
}

const KpiChangeIndicator: React.FC<IKpiChangeIndicatorProps> = ({ currentValue, changePercent }) => {
  if (changePercent === null) {
    return (
      <p className="m-0 font-pt-sans text-[13px] leading-normal tracking-[0.52px] text-center text-gray-100">
        No data in previous period
      </p>
    );
  }

  if (currentValue === 0) {
    return (
      <p className="m-0 font-pt-sans text-[13px] leading-normal tracking-[0.52px] text-center text-gray-100">
        No data in current period
      </p>
    );
  }

  return <ChangePill changePercent={changePercent} />;
};

export const KpiCards: React.FC<IKpiCardsProps> = ({
  totalBookings,
  totalValueCents,
  bookingsChangePercent,
  valueChangePercent,
}) => {
  const { isMobile } = useResponsive();
  const formattedTotalSales = isMobile ? formatPriceCentsInMillions(totalValueCents) : formatPriceCents(totalValueCents);
  const formattedTotalBookings = isMobile ? formatPriceInMillions(totalBookings) : formatPrice(totalBookings, 0);

  return (
    <div className="bg-white border border-gray-20 border-solid flex items-start gap-[40px] p-[20px] w-full">
      <div className="flex flex-1 flex-col items-center gap-[10px] min-w-0">
        <p className="m-0 font-pt-sans text-[13px] leading-[22px] tracking-[0.52px] uppercase text-black">
          Total sales
        </p>
        <p className="m-0 font-pt-sans font-bold text-[21px] leading-normal text-black text-center tabular-nums">
          {/* API does not return currency code, so we use USD as default */}
          {`${getCurrencySymbol('USD')}`}&nbsp;{formattedTotalSales}
        </p>
        <KpiChangeIndicator currentValue={totalValueCents} changePercent={valueChangePercent} />
      </div>

      <div className="w-[3px] h-[94px] bg-gray-20 self-center shrink-0" />

      <div className="flex flex-1 flex-col items-center gap-[10px] min-w-0">
        <p className="m-0 font-pt-sans text-[13px] leading-[22px] tracking-[0.52px] uppercase text-black text-center">
          Total bookings
        </p>
        <p className="m-0 font-pt-sans font-bold text-[21px] leading-normal text-black text-center tabular-nums">
          {formattedTotalBookings}
        </p>
        <KpiChangeIndicator currentValue={totalBookings} changePercent={bookingsChangePercent} />
      </div>
    </div>
  );
};
